import {
  Camera,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  FilePlus,
  ImagePlus,
  RotateCw,
  Trash2,
} from "lucide-react";
import { useRef } from "react";
import { plural } from "../lib/format";
import {
  includedPages,
  needsAttention,
  type PrepSource,
  type PrepState,
} from "../lib/prepare/usePagePreparation";
import { useDocumentPicker } from "./UploadZone";
import { Alert, ICON } from "./ui";

// Preview of the prepared pages before anything is processed (spec §1A "Încarcă"): pages appear one by one as
// they are prepared in the browser, with what was found (blank, dark, reflections, small text, out of focus,
// turned) and optional actions per page: move, turn, photograph again, remove. It replaces the first choice
// (upload zone): its one "more" button, at the top so it stays in sight on a phone, adds the next page the same
// way the first came in (next photo, more photos, another file). Nothing is a required step: a readable
// document goes on by itself (or with the one button that ends a series of photos).

const BADGE = {
  blank: "Pagină goală",
  unclear: "De refotografiat",
  failed: "Nu s-a putut converti",
} as const;

const MORE = {
  camera: { label: "Fotografiază pagina următoare", Icon: Camera },
  gallery: { label: "Adaugă alte fotografii", Icon: ImagePlus },
  files: { label: "Adaugă alt fișier", Icon: FilePlus },
} as const;

export interface PagePreviewActions {
  onToggle: (key: string) => void;
  onRotate: (key: string) => void;
  onMove: (key: string, delta: -1 | 1) => void;
  onRemove: (key: string) => void;
  onRetake: (key: string, photo: File, source: PrepSource) => void;
  onAdd: (files: File[], source: PrepSource) => void;
}

export function PagePreview({
  state,
  actions,
  onCancel,
  onContinue,
}: {
  state: PrepState;
  actions: PagePreviewActions;
  onCancel: () => void;
  /** The button that sends the pages (Import page); absent in the onboarding, which has its own. */
  onContinue?: () => void;
}) {
  const { phase, pages, total } = state;
  const included = includedPages(state).length;
  const left = pages.filter((p) => !p.include).length;
  const uploading = phase === "uploading";
  const done = uploading ? state.uploaded : pages.length;
  const of = uploading ? included : Math.max(total, pages.length);
  const progress = of ? Math.round((done / of) * 100) : 0;
  const title =
    phase === "preparing"
      ? `Pregătim paginile… ${done} din ${of}`
      : uploading
        ? `Se încarcă paginile… ${done} din ${of}`
        : `${plural(pages.length, "pagină pregătită", "pagini pregătite")}${left ? `, ${left} ${left === 1 ? "omisă" : "omise"}` : ""}`;
  const busy = phase === "preparing" || uploading;
  const attention = needsAttention(state);
  // One hidden picker for adding pages, one for photographing a given page again.
  const adder = useDocumentPicker(actions.onAdd);
  const retakeKey = useRef("");
  const retaker = useDocumentPicker((files, source) => {
    const [photo] = files;
    if (photo && retakeKey.current) actions.onRetake(retakeKey.current, photo, source);
  });
  const retake = (key: string) => {
    retakeKey.current = key;
    retaker.open("camera");
  };
  const source = state.source ?? "files";
  const more = MORE[source];

  return (
    <section className="card stack" aria-label="Previzualizarea paginilor" style={{ gap: 14 }}>
      {adder.inputs}
      {retaker.inputs}
      <div className="stack" style={{ gap: 6 }}>
        <div className="row" style={{ justifyContent: "space-between", fontSize: 14, gap: 8 }}>
          <span id="prep-title" style={{ fontWeight: 600 }}>
            {title}
          </span>
          {busy && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>
              Anulează
            </button>
          )}
        </div>
        {busy && (
          <div
            className="progress"
            role="progressbar"
            aria-labelledby="prep-title"
            aria-valuenow={progress}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div style={{ width: `${progress}%` }} />
          </div>
        )}
        <span className="caption muted">
          Paginile se pregătesc pe dispozitivul tău; nimic nu se trimite până nu se încarcă importul.
        </span>
      </div>

      {!uploading && (
        <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
          <button type="button" className="btn btn-secondary" onClick={() => adder.open(source)}>
            <more.Icon {...ICON} aria-hidden="true" />
            {more.label}
          </button>
        </div>
      )}

      {state.failed.map((f) => (
        <Alert key={f.name} tone="error" title={f.name}>
          {f.message}
        </Alert>
      ))}
      {state.error && (
        <Alert tone="error" live>
          {state.error}
        </Alert>
      )}
      {phase === "ready" && pages.length > 0 && included === 0 && (
        <Alert tone="warning" live>
          Nicio pagină nu este inclusă. Include paginile cu meniul sau adaugă fotografii noi.
        </Alert>
      )}
      {phase === "ready" && attention && included > 0 && (
        <Alert tone="warning" live title="Unele pagini au nevoie de o privire">
          O fotografie mai clară dă prețuri corecte din prima: refotografiază paginile marcate. Le poți și
          roti, muta sau elimina; o pagină greu de citit poate fi procesată oricum, dar vei avea mai multe de
          verificat.
        </Alert>
      )}

      <ol className="page-grid">
        {pages.map((p, i) => {
          const label = p.pages > 1 ? `${p.name}, pagina ${p.page}` : p.name;
          const locked = uploading || !!state.turning;
          return (
            <li key={p.key} className={`page-card${p.include ? "" : " left-out"}`}>
              <div className="page-thumb">
                {p.thumb ? (
                  <img src={p.thumb} alt={`Previzualizare: ${label}`} />
                ) : (
                  <CircleAlert {...ICON} aria-hidden="true" />
                )}
                <span className="page-number" aria-hidden="true">
                  {i + 1}
                </span>
                {p.status !== "ok" && (
                  <span className={`page-badge ${p.status}`}>{BADGE[p.status as keyof typeof BADGE]}</span>
                )}
              </div>
              <span className="caption" style={{ overflowWrap: "anywhere" }}>
                {p.pages > 1 ? `Pagina ${p.page} · ${p.name}` : p.name}
              </span>
              {p.reasons.length > 0 && p.status !== "blank" && (
                <span className="caption muted">{p.reasons.join(" ")}</span>
              )}
              {p.kind === "image" && p.status === "unclear" && (
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  disabled={locked}
                  onClick={() => retake(p.key)}
                >
                  <Camera {...ICON} aria-hidden="true" />
                  Refotografiază
                </button>
              )}
              {!p.include && p.image && (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  aria-label={`Include: ${label}`}
                  disabled={uploading}
                  onClick={() => actions.onToggle(p.key)}
                >
                  Include
                </button>
              )}
              <div className="page-actions">
                <button
                  type="button"
                  className="icon-btn sm"
                  aria-label={`Mută mai devreme: ${label}`}
                  title="Mută mai devreme"
                  disabled={locked || i === 0}
                  onClick={() => actions.onMove(p.key, -1)}
                >
                  <ChevronLeft {...ICON} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="icon-btn sm"
                  aria-label={`Mută mai târziu: ${label}`}
                  title="Mută mai târziu"
                  disabled={locked || i === pages.length - 1}
                  onClick={() => actions.onMove(p.key, 1)}
                >
                  <ChevronRight {...ICON} aria-hidden="true" />
                </button>
                {p.image && (
                  <button
                    type="button"
                    className="icon-btn sm"
                    aria-label={`Rotește: ${label}`}
                    title="Rotește"
                    disabled={locked}
                    onClick={() => actions.onRotate(p.key)}
                  >
                    <RotateCw
                      {...ICON}
                      aria-hidden="true"
                      className={state.turning === p.key ? "spin" : undefined}
                    />
                  </button>
                )}
                {p.kind === "image" && p.status !== "unclear" && (
                  <button
                    type="button"
                    className="icon-btn sm"
                    aria-label={`Refotografiază: ${label}`}
                    title="Refotografiază"
                    disabled={locked}
                    onClick={() => retake(p.key)}
                  >
                    <Camera {...ICON} aria-hidden="true" />
                  </button>
                )}
                <button
                  type="button"
                  className="icon-btn sm"
                  aria-label={`Elimină: ${label}`}
                  title="Elimină"
                  disabled={locked}
                  onClick={() => actions.onRemove(p.key)}
                >
                  <Trash2 {...ICON} aria-hidden="true" />
                </button>
              </div>
            </li>
          );
        })}
      </ol>

      {onContinue && phase === "ready" && included > 0 && (
        <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
          <button type="button" className="btn btn-primary" onClick={onContinue}>
            {attention
              ? `Continuă cu ${plural(included, "pagină", "pagini")}`
              : `Procesează meniul (${plural(included, "pagină", "pagini")})`}
          </button>
          <button type="button" className="btn btn-secondary" onClick={onCancel}>
            Alege alt document
          </button>
        </div>
      )}
    </section>
  );
}
